import { useState } from 'react';
import { Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { EyebrowLabel, formatDateTime, space } from '@viq-admin/ui';
import { displayName } from './labels';
import { LEAD_RULES_COPY } from '../leadRules/copy';
import type { NotesCounterState } from '../leadRules/notesCounter';
import type { LeadDetail } from './types';

interface Props {
  rawnotes: LeadDetail['rawnotes'];
  /**
   * Optional edit closure. Absent → today's exact read-only render.
   * NOT offered for `captureMethod === 'system'`: those are platform-written
   * provenance records (e.g. a lead move), not human capture, and rewriting
   * one would falsify the audit trail it exists to provide.
   */
  onEdit?: (noteId: string, currentText: string) => void;
  /**
   * Optional "Add note" closure. Absent → no control, today's exact render.
   * Offered whether or not the lead has notes yet: with none there is nothing
   * to edit, which is the case this exists for — a lead captured without any
   * note could otherwise never gain one from the admin side.
   */
  onAdd?: () => void;
  /**
   * Company lead rules notes progress (spec 2026-09-14 §3.4), from
   * `notesCounterState`. Absent → no counter: rules off, no notes minimum, or
   * an older backend.
   */
  counter?: NotesCounterState;
}

/** One note's body, with the "Edited"/"View original" provenance affordance. */
function NoteBody({ note }: { note: LeadDetail['rawnotes'][number] }) {
  const [showOriginal, setShowOriginal] = useState(false);
  return (
    <>
      {/* pre-wrap is load-bearing, not cosmetic: a move note carries the
          hard-deleted discovery answers as newline-separated Q/A lines,
          which antd Typography would otherwise collapse into one run-on
          paragraph. `anywhere` keeps a pasted URL from widening the card. */}
      <Typography.Paragraph
        data-testid="rawnote-text"
        style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
      >
        {note.text}
      </Typography.Paragraph>
      {/* originalText is null for a never-edited note; test != null, not
          truthiness — the notes.original_text column is NOT NULL once set,
          so a falsy-but-edited value can't occur, but `!= null` is still the
          contract this field promises (see Task 9's mapper). */}
      {note.originalText != null && (
        <>
          <Space size={4}>
            <Tag>Edited</Tag>
            <Button type="link" size="small" onClick={() => setShowOriginal((v) => !v)}>
              {showOriginal ? 'Hide original' : 'View original'}
            </Button>
          </Space>
          {showOriginal && (
            <Typography.Paragraph
              type="secondary"
              style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {note.originalText}
            </Typography.Paragraph>
          )}
        </>
      )}
    </>
  );
}

/**
 * Verbatim capture log for a lead. Renders `rawnotes[].text` — the words the
 * rep dictated, and the system notes the platform writes.
 *
 * Deliberately NOT the same thing as AiSummary, which renders `rawnotes[].summary`.
 * `summary` is the enrichment output and is null until enrichment runs (often
 * forever), so before this panel existed a lead's notes were fetched by both
 * planes, mapped into memory, and then never painted. The two panels coexist on
 * purpose: the summary is a distillation and must stay labelled as one, so this
 * panel never falls back to `summary` and AiSummary never falls back to `text`.
 *
 * Shared by both shells with no api prop — unlike card images (console-only by
 * design), rawnotes are already returned to the portal and already sync to the
 * rep's device, so withholding them from the portal UI protects nothing.
 *
 * Also surfaces edit provenance via `NoteBody`: a note with a non-null
 * `originalText` (Task 9) was admin-edited, and gets an "Edited" badge with a
 * "View original" toggle in EVERY render — including the read-only one, when
 * `onEdit` is absent. Provenance is not an edit affordance: a `stakeholder`
 * who cannot edit still needs to know the text in front of them was rewritten,
 * so the badge is never gated on `onEdit`. The edit control itself IS gated —
 * on `onEdit` being supplied, AND on `captureMethod !== 'system'`, since a
 * system note (e.g. the one the lead-move feature writes, which after a move
 * is the only surviving copy of the lead's deleted Discovery answers) is
 * platform-written provenance, not human capture, and rewriting one would
 * falsify the audit trail it exists to provide.
 */
export function NotesPanel({ rawnotes, onEdit, onAdd, counter }: Props) {
  return (
    <Card size="small">
      {/* Same header layout as AiSummary: the eyebrow on the left, the extra on the right. */}
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <EyebrowLabel>Notes</EyebrowLabel>
        <Space size={space.sm}>
          {counter && (
            <Tooltip title={LEAD_RULES_COPY.notesCounterHelp(counter.need)}>
              <Typography.Text data-testid="notes-counter" data-tone={counter.tone} type={counter.tone} strong>
                {`${counter.have} / ${counter.need}`}
              </Typography.Text>
            </Tooltip>
          )}
          {onAdd && (
            <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
              Add note
            </Button>
          )}
        </Space>
      </Space>
      {rawnotes.length === 0 ? (
        <Typography.Text type="secondary">No notes captured.</Typography.Text>
      ) : (
        // No client-side sort: the backend already returns created_at DESC on
        // both planes, and re-sorting here would silently paper over a change
        // in that ordering instead of surfacing it.
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {rawnotes.map((n) => (
            <div key={n.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: space.sm,
                }}
              >
                <Typography.Text type="secondary">
                  {displayName('capture', n.captureMethod)}
                </Typography.Text>
                <Space size={4}>
                  {/* Date + TIME, not formatDate: a booth conversation and the move
                      that follows it land minutes apart on the same day, and a
                      date-only stamp cannot order them for the reader. */}
                  <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(n.createdAt)}
                  </Typography.Text>
                  {onEdit && n.captureMethod !== 'system' && (
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      aria-label="Edit note"
                      onClick={() => onEdit(n.id, n.text)}
                    />
                  )}
                </Space>
              </div>
              <NoteBody note={n} />
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
}
